import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "../components/ui/table";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../components/ui/select";
import { 
  FileText, 
  Clock, 
  CheckCircle, 
  XCircle, 
  DollarSign, 
  TrendingUp,
  LogOut,
  Search,
  Eye,
  Loader2,
  Users,
  BarChart3,
  Brain,
  ChartLine
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_small-biz-loans/artifacts/ojg09jbh_logo.png";

const LoanOfficerDashboard = () => {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [appsRes, statsRes] = await Promise.all([
        axios.get(`${API}/loans/all`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/stats/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      // Handle paginated response - extract applications array
      const appsData = appsRes.data.applications || appsRes.data;
      setApplications(Array.isArray(appsData) ? appsData : []);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "approved":
        return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Approved</Badge>;
      case "rejected":
        return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Rejected</Badge>;
      case "pending_review":
        return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Under Review</Badge>;
      default:
        return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Pending</Badge>;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const filteredApplications = applications.filter(app => {
    const matchesSearch = 
      app.business_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.application_id?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || app.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-12 w-12 animate-spin text-[#0A2540]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="loan-officer-dashboard">
      {/* Header */}
      <header className="bg-[#0A2540] text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="Tzur Finances" className="h-10 brightness-0 invert" />
              <Badge variant="secondary" className="bg-amber-500 text-[#0A2540] hover:bg-amber-500">
                Loan Officer
              </Badge>
            </div>
            
            <div className="flex items-center gap-4">
              <Link to="/officer/credit-decisioning">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-white hover:bg-white/10"
                  data-testid="credit-decisioning-btn"
                >
                  <Brain className="h-4 w-4 mr-2" />
                  Credit Decisioning
                </Button>
              </Link>
              <Link to="/officer/analytics">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-white hover:bg-white/10"
                  data-testid="analytics-btn"
                >
                  <ChartLine className="h-4 w-4 mr-2" />
                  Analytics
                </Button>
              </Link>
              <span className="text-sm text-gray-300 hidden sm:block">
                {user?.name}
              </span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleLogout}
                className="text-white hover:bg-white/10"
                data-testid="logout-btn"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#0A2540]">Loan Applications</h1>
          <p className="text-gray-600 mt-1">Review and manage loan applications</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-[#0A2540]/10 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-[#0A2540]" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total</p>
                  <p className="text-2xl font-bold text-[#0A2540] font-mono" data-testid="total-apps">
                    {stats?.total_applications || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Clock className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Pending</p>
                  <p className="text-2xl font-bold text-amber-600 font-mono" data-testid="pending-apps">
                    {stats?.pending_review || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Approved</p>
                  <p className="text-2xl font-bold text-emerald-600 font-mono" data-testid="approved-apps">
                    {stats?.approved || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center">
                  <XCircle className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Rejected</p>
                  <p className="text-2xl font-bold text-red-600 font-mono" data-testid="rejected-apps">
                    {stats?.rejected || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-[#0A2540]/10 flex items-center justify-center">
                  <BarChart3 className="h-5 w-5 text-[#0A2540]" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Approval Rate</p>
                  <p className="text-2xl font-bold text-[#0A2540] font-mono" data-testid="approval-rate">
                    {stats?.approval_rate || 0}%
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Total Approved Amount */}
        <Card className="bg-gradient-to-r from-[#0A2540] to-[#1e3a8a] text-white mb-8">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-300 text-sm">Total Approved Amount</p>
                <p className="text-4xl font-bold font-mono mt-1" data-testid="total-approved-amount">
                  {formatCurrency(stats?.total_approved_amount || 0)}
                </p>
              </div>
              <DollarSign className="h-16 w-16 text-white/20" />
            </div>
          </CardContent>
        </Card>

        {/* Applications Table */}
        <Card className="bg-white">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <CardTitle className="text-xl text-[#0A2540]">All Applications</CardTitle>
                <CardDescription>Click on an application to review</CardDescription>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search applications..."
                    className="pl-10 w-full sm:w-64"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    data-testid="search-input"
                  />
                </div>
                
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full sm:w-40" data-testid="status-filter">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="pending_review">Under Review</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filteredApplications.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900">No applications found</h3>
                <p className="text-gray-500 mt-1">
                  {searchTerm || statusFilter !== "all" 
                    ? "Try adjusting your search or filter" 
                    : "No loan applications have been submitted yet"}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Application ID</TableHead>
                      <TableHead>Business</TableHead>
                      <TableHead>Loan Type</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Term</TableHead>
                      <TableHead>Risk Score</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredApplications.map((app) => (
                      <TableRow 
                        key={app.application_id}
                        className="cursor-pointer hover:bg-gray-50"
                        data-testid={`app-row-${app.application_id}`}
                      >
                        <TableCell className="font-mono text-sm">
                          {app.application_id.slice(0, 12)}...
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{app.business_name}</p>
                            <p className="text-sm text-gray-500">{app.user_name}</p>
                          </div>
                        </TableCell>
                        <TableCell>{app.loan_type}</TableCell>
                        <TableCell className="text-right font-mono font-semibold">
                          {formatCurrency(app.amount)}
                        </TableCell>
                        <TableCell>{app.term_months}mo</TableCell>
                        <TableCell>
                          {app.risk_score ? (
                            <span className={`font-mono font-semibold ${
                              app.risk_score >= 70 ? 'text-emerald-600' :
                              app.risk_score >= 50 ? 'text-amber-600' : 'text-red-600'
                            }`}>
                              {app.risk_score}/100
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>{getStatusBadge(app.status)}</TableCell>
                        <TableCell className="text-sm text-gray-500">
                          {formatDate(app.created_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Link to={`/officer/review/${app.application_id}`}>
                            <Button 
                              variant="outline" 
                              size="sm"
                              data-testid={`review-btn-${app.application_id}`}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Review
                            </Button>
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default LoanOfficerDashboard;
